// src/pages/WifiPlansPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../layouts/PageContainer';
import PageHeader from '../components/Nav/PageHeader';
import { useConfig } from '../DataStore/ConfigContext';
import PlanCard from '../components/Voucher/PlanCard';
import { Skeleton, SkeletonBar, SkeletonPill } from '../util/skeleton/Skeleton';
import StarterPanel from '../components/Dashboard/StarterPanel';
import NewVoucher from '../components/Voucher/NewVoucher';

const SkeletonPlanCard = () => (
  <div className="col-lg-3 col-sm-6">
    <div className="card card-pricing custom-card plan-card">
      {/* Header skeleton */}
      <div className="plan-header text-center">
        <Skeleton w="60%" h={18} className="mx-auto d-block" />
        <div className="d-flex justify-content-center align-items-end mt-2">
          <Skeleton w="48px" h={16} className="mr-2" />
          <Skeleton w="120px" h={44} />
          <Skeleton w="70px" h={14} className="ml-2" />
        </div>
        <Skeleton w="64px" h={10} className="mx-auto d-block mt-2" />
      </div>

      {/* Scrollable body skeleton */}
      <div className="plan-body">
        {[...Array(9)].map((_, i) => (
          <SkeletonBar key={i} w={`${80 - (i % 5) * 8}%`} h={12} className="mb-2" />
        ))}
      </div>

      {/* Footer skeleton */}
      <div className="plan-footer text-center">
        <SkeletonPill w="100%" h={44} />
      </div>
    </div>
  </div>
);


const WifiPlansPage = () => {
  const { wifiPlans, getWifiPlans } = useConfig();
  const [showManual, setShowManual] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => { getWifiPlans?.(); }, [getWifiPlans]);

  const plans = useMemo(() => {
    if (!Array.isArray(wifiPlans)) return null;
    // Only visible plans; keep a sensible order (sort_order -> price -> name)
    return [...wifiPlans]
      .filter(p => String(p.is_visible) === '1')
      .sort((a, b) =>
        (Number(a.sort_order ?? 9999) - Number(b.sort_order ?? 9999)) ||
        (Number(a.price ?? 0) - Number(b.price ?? 0)) ||
        String(a.name).localeCompare(String(b.name))
      );
  }, [wifiPlans]);

  const handleGenerate = (plan) => {
    setShowManual(true);
    setSelectedPlan(plan);
  };

  return (<>
    <PageContainer>
      <PageHeader
        title="WiFi Plans"
        breadcrumb={<li className="breadcrumb-item active" aria-current="page">WiFi Plans</li>}
        primaryButtons={[
            {
                label: 'Refresh',
                icon: 'fas fa-sync',
                className: 'btn-outline-secondary',
                onClick: () => getWifiPlans?.(true)
            }
    ]}
      />

       <div className="row">
        <StarterPanel greetingText='Hi'/>
        <div className="col-lg-8 mb-4"></div>
      </div>

      <div className="row">
        {/* Loading */}
        {plans === null && Array.from({ length: 4 }).map((_, i) => <SkeletonPlanCard key={i} />)}

        {/* Empty */}
        {Array.isArray(plans) && plans.length === 0 && (
          <div className="col-12 text-center text-muted py-5">No visible plans found.</div>
        )}

        {/* Plans (adopted template) */}
        {Array.isArray(plans) && plans.map((plan, idx) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            variant={(idx % 4) + 1}   // use your 1..4 template variants
            selected={selectedPlan?.id === plan.id}
            onGenerate={handleGenerate}
          />
        ))}
      </div>
    </PageContainer>
     <NewVoucher
      show={showManual}
      onClose={() => setShowManual(false)}
      onSuccess={() => {
            setShowManual(false);
            getWifiPlans(true);
        }}
     selection={selectedPlan}
    />
    </>
  );
};

export default WifiPlansPage;
